import { createContext, useContext, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

type ThemeMode = "light" | "dark" | "cream";

type ThemeContextType = {
  darkThemeEnabled: boolean;
  themeMode: ThemeMode;
  setDarkThemeEnabled: (enabled: boolean) => void;
  setThemeMode: (mode: ThemeMode) => void;
  loading: boolean;
};

const ThemeContext = createContext<ThemeContextType | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [darkThemeEnabled, setDarkThemeEnabledState] = useState<boolean>(false);
  const [themeMode, setThemeModeState] = useState<ThemeMode>("light");

  const { data, isLoading } = useQuery({
    queryKey: ["theme-setting"],
    queryFn: async () => {
      try {
        const res = await api<{ darkThemeEnabled: boolean; themeMode: ThemeMode }>(
          "/api/settings/theme",
        );
        return res;
      } catch {
        return { darkThemeEnabled: false, themeMode: "light" as ThemeMode };
      }
    },
    staleTime: 60_000,
  });

  useEffect(() => {
    if (data) {
      setDarkThemeEnabledState(data.darkThemeEnabled);
      setThemeModeState(data.themeMode);
    }
  }, [data]);

  useEffect(() => {
    const root = document.documentElement;
    // Remove all theme classes
    root.classList.remove("dark", "theme-cream");

    if (darkThemeEnabled) {
      root.classList.add("dark");
    } else if (themeMode === "cream") {
      root.classList.add("theme-cream");
    }
  }, [darkThemeEnabled, themeMode]);

  const setDarkThemeEnabled = (enabled: boolean) => {
    setDarkThemeEnabledState(enabled);
  };

  const setThemeMode = (mode: ThemeMode) => {
    setThemeModeState(mode);
  };

  return (
    <ThemeContext.Provider
      value={{
        darkThemeEnabled,
        themeMode,
        setDarkThemeEnabled,
        setThemeMode,
        loading: isLoading,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
